import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { supabase } from '@lib/supabase';
import type { User } from '@core/types/common.types';
import type { Session } from '@supabase/supabase-js';

function translateAuthError(message: string): string {
  const lower = message.toLowerCase();
  if (lower.includes('email rate limit') || lower.includes('over_email_send_rate_limit')) {
    return 'Límite de correos alcanzado. Espera unos minutos antes de intentarlo de nuevo.';
  }
  if (lower.includes('user already registered') || lower.includes('already registered')) {
    return 'Este correo ya está registrado. Intenta iniciar sesión.';
  }
  if (lower.includes('invalid login credentials') || lower.includes('invalid credentials')) {
    return 'Correo o contraseña incorrectos.';
  }
  if (lower.includes('email not confirmed')) {
    return 'Confirma tu correo electrónico antes de iniciar sesión.';
  }
  if (lower.includes('password should be at least')) {
    return 'La contraseña debe tener al menos 6 caracteres.';
  }
  if (lower.includes('signup is disabled') || lower.includes('signups not allowed')) {
    return 'El registro está temporalmente deshabilitado.';
  }
  if (lower.includes('network') || lower.includes('fetch')) {
    return 'Error de red. Verifica tu conexión e intenta de nuevo.';
  }
  return message;
}

interface AuthState {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextValue extends AuthState {
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string, userData: Partial<User>) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: string | null }>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Direct fetch bypasses the Supabase auth client lock held during onAuthStateChange
// callbacks. Using supabase.from() inside those callbacks deadlocks because the
// internal getSession() tries to re-acquire the same lock. The access_token from
// the event session is passed explicitly to avoid any lock contention.
async function fetchUserProfile(userId: string, accessToken: string): Promise<User | null> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

    const response = await fetch(
      `${supabaseUrl}/rest/v1/usuarios?id=eq.${userId}&select=*&limit=1`,
      {
        headers: {
          apikey: supabaseAnonKey,
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
        },
      }
    );

    if (!response.ok) {
      console.error('Error fetching user profile:', await response.text());
      return null;
    }

    const users = await response.json() as User[];
    return users[0] ?? null;
  } catch (err) {
    console.error('Exception in fetchUserProfile:', err);
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    isLoading: true,
    isAuthenticated: false,
  });

  useEffect(() => {
    // Set up onAuthStateChange FIRST, then initialize session.
    // This avoids a race condition where SIGNED_IN fires before the
    // listener is registered when signInWithPassword resolves.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'INITIAL_SESSION') {
          if (!session?.user) {
            setState({ user: null, session: null, isLoading: false, isAuthenticated: false });
            return;
          }

          const nowSec = Math.floor(Date.now() / 1000);
          const tokenExpired = session.expires_at ? session.expires_at <= nowSec : false;

          if (tokenExpired) {
            // Supabase will auto-refresh and fire TOKEN_REFRESHED shortly.
            // Stay in isLoading=true; SIGNED_OUT fires if refresh token is also expired.
            return;
          }

          const userProfile = await fetchUserProfile(session.user.id, session.access_token);
          setState({ user: userProfile, session, isLoading: false, isAuthenticated: !!userProfile });

        } else if (
          (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') &&
          session?.user
        ) {
          const userProfile = await fetchUserProfile(session.user.id, session.access_token);
          setState({ user: userProfile, session, isLoading: false, isAuthenticated: !!userProfile });

        } else if (event === 'SIGNED_OUT') {
          setState({ user: null, session: null, isLoading: false, isAuthenticated: false });
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      return { error: translateAuthError(error.message) };
    }

    return { error: null };
  }, []);

  const signUp = useCallback(async (
    email: string,
    password: string,
    userData: Partial<User>
  ) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          nombre: userData.nombre,
          apellido: userData.apellido,
          telefono: userData.telefono,
        },
      },
    });

    if (error) {
      return { error: translateAuthError(error.message) };
    }

    return { error: null };
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/password-reset`,
    });

    if (error) {
      return { error: translateAuthError(error.message) };
    }

    return { error: null };
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    ...state,
    signIn,
    signUp,
    signOut,
    resetPassword,
  }), [state, signIn, signUp, signOut, resetPassword]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}
